import { showCustomToast } from 'common/components/CustomToast/utils';
import { useMutation } from '@apollo/client';
import {
  Callout,
  DirectionalHint,
  IDropdownOption,
  PrimaryButton,
  DefaultButton,
  ProgressIndicator,
  Stack,
  Text,
  IPanelHeaderRenderer,
  IconButton,
} from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { AccountingDetails } from 'common/components/AccountingDetails';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import {
  EntityDocumentUpdate,
  EntityDocumentUpdateVariables,
  EntityDocumentUpdate_entityDocumentUpdate_entityDocuments,
} from 'common/graphql/__generated__/EntityDocumentUpdate';
import { EntityDocumentPatch } from 'common/types/globalTypes';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';

import { validationSchema } from '../documentValidations';
import { EntityDocumentValues } from '../types';
import { EntityDocumentsPersonalPool_entityDocuments_nodes } from '../__generated__/EntityDocumentsPersonalPool';

import DOCUMENTSLIST from '../MyDocumentsList/PersonalDocuments.graphql';
import RETIRE_BALANCE from './EntityDocumentRetireBalancePersonalPool.graphql';
import UPDATE_DOC from 'common/graphql/EntityDocumentUpdate.graphql';
import { useStyles } from './index.styles';
import { DocumentTypeOptions, EntityAction, EntityType, PanelCommonProps } from 'common/types/utility';
import {
  EntityDocumentRetireBalancePersonalPool,
  EntityDocumentRetireBalancePersonalPoolVariables,
} from './__generated__/EntityDocumentRetireBalancePersonalPool';
import { FooterActionBar } from 'common/components/FooterActionBar';
import DraggablePanel from 'common/components/DraggablePanel';
import { PanelHeader } from 'common/components/PanelHeader';
import { useCommonStyles } from 'common/styles';
import { DocumentViewModalState } from 'common/components/DocumentList';
import { DocumentViewModal, VIEW_DOC_MODAL_WIDTH } from 'common/components/DocumentList/DocumentViewModal';
import { getWindowDimensions } from 'common/utils/columnUtilities';
import DELETE_DOCUMENTS from 'common/graphql/EntityDocumentDelete.graphql';
import {
  EntityDocumentDelete,
  EntityDocumentDeleteVariables,
} from 'common/graphql/__generated__/EntityDocumentDelete';
import { ActionMessageModal } from 'common/components/ActionMessageModal';
import { DOCUMENT_INITIAL_VALUES } from '../constant';
import { DocumentDetailPanleFormFields } from './DocumentDetailPanleFormFields';


interface MyDocumentsViewProps {
  visible: boolean;
  onDismiss: () => void;
  documentOptions?: DocumentTypeOptions[];
  companyCurrencies?: IDropdownOption[];
  defaultDocumentTypeId?: number;
  documentData: EntityDocumentsPersonalPool_entityDocuments_nodes;
  onUpdate?: (
    updatedDocuments: EntityDocumentUpdate_entityDocumentUpdate_entityDocuments[]
  ) => void;
}

export const MyDocumentsView: React.FC<MyDocumentsViewProps> = ({
  visible,
  onDismiss,
  documentOptions,
  companyCurrencies,
  defaultDocumentTypeId,
  documentData,
  onUpdate,
}) => {
  const styles = useStyles();
  const commonStyles = useCommonStyles();
  const [updateDocument, { loading: updateDocLoading }] = useMutation<
    EntityDocumentUpdate,
    EntityDocumentUpdateVariables
  >(UPDATE_DOC, { errorPolicy: 'all' });

  const [retireBalance, { loading: retireBalanceLoading }] = useMutation<
    EntityDocumentRetireBalancePersonalPool,
    EntityDocumentRetireBalancePersonalPoolVariables
  >(RETIRE_BALANCE, { errorPolicy: 'all' });

  const [hideConfirmDialog, setHideConfirmDialog] = useState<boolean>(true);
  const toggleConfirmDialog = () => setHideConfirmDialog((prev) => !prev);
  const [isCalloutVisible, setIsCalloutVisible] = useState(false);
  const calloutId = useId(`callOutId${documentData?.id!}`);
  const [docViewState, setDocViewState] = useState<DocumentViewModalState>({
    isOpen: false,
    _fileType: 'pdf',
  });
  const [fileSelected, setFileSelected] = useState<File[]>([]);
  const [isAccountingDocument, setIsAccountingDocument] = useState(false);
  const [documentType, setDocumentType] = useState<DocumentTypeOptions>();
  const [isDisabled, setIsDisabled] = useState(false);

  const isDocumentProtected = documentData ? !documentData?._isUpdatable : false;

  const initialValues: EntityDocumentValues = documentData
    ? {
      fileIndexInformation: documentData.fileIndexInformation,
      _documentContents: documentData._documentContents,
      comment: documentData.comment,
      entityDocumentTypeId: documentData.entityDocumentTypeId,
      indexTransactionDate: documentData.indexTransactionDate
        ? documentData.indexTransactionDate
        : null,
      indexName: documentData.indexName,
      indexDescription: documentData.indexDescription,
      indexReferenceNumber: documentData.indexReferenceNumber,
      indexAmount: documentData.indexAmount,
      indexCurrencyId: documentData.indexCurrencyId,
      extractionTypeId: documentData.extractionTypeId,
    }
    : DOCUMENT_INITIAL_VALUES;

  const clearData = () => {
    setIsAccountingDocument(false);
    setDocumentType(undefined);
    setFileSelected([]);
  };
  const { width } = getWindowDimensions();
  const documentViewPosition = width
    ? width - (900 + VIEW_DOC_MODAL_WIDTH)
    : 50;
  const handleUpdate = async (values: EntityDocumentValues) => {
    const { ...item } = values;
    const entityDocumentPatch: EntityDocumentPatch = Object.entries(item).reduce(
      (res, [key, val]) => {
        if (val !== initialValues[key as keyof EntityDocumentValues]) {
          return { ...res, [key]: val };
        }
        return res;
      },
      { indexTransactionDate: values.indexTransactionDate } as EntityDocumentPatch
    );
    const { errors, data } = await updateDocument({
      variables: {
        input: {
          entityDocumentsUpdate: [
            {
              id: documentData?.id,
              rowTimestamp: documentData?._rowTimestamp,
              entityDocumentPatch: entityDocumentPatch,
            },
          ],
        },
      },
      update(cache, { data: dataSet }) {
        cache.modify({
          fields: {
            PersonalPool(existinPools = []) {
              const NewPoolData = cache.writeFragment({
                data: dataSet?.entityDocumentUpdate?.entityDocuments,
                fragment: DOCUMENTSLIST,
              });
              return [...existinPools, NewPoolData];
            },
          },
        });
      },
    });

    if (errors?.length) {
      showCustomToast({ content: errors[0].message, appearance: 'error' });
    } else {
      onUpdate?.(data?.entityDocumentUpdate?.entityDocuments!);
      showCustomToast({ content: 'Document updated', appearance: 'success' });
    }
  };

  const [deleteDocument, { loading: deleteLoading }] = useMutation<
    EntityDocumentDelete,
    EntityDocumentDeleteVariables
  >(DELETE_DOCUMENTS, { errorPolicy: 'all' });

  if (!visible) return null;


  const handleDeleteDocument = async () => {
    const { errors } = await deleteDocument({
      variables: {
        input: {
          entityDelete: [
            { id: documentData?.id, rowTimestamp: documentData?._rowTimestamp! },
          ],
        },
      },
      update: (cache, { data }) => {
        const deletedId = data?.entityDocumentDelete?.deletedEntities?.[0]?.id;
        if (deletedId) {
          cache.evict({ id: cache.identify({ __typename: 'EntityDocument', id: deletedId }) });
          cache.gc();
        }
      },
    });

    if (errors?.length) {
      showCustomToast({ content: errors[0].message, appearance: 'error' });
    } else {
      showCustomToast({ content: 'Record deleted Successfully', appearance: 'success' });
      onDismiss();
    }
  };

  const _onRenderHeader: IPanelHeaderRenderer = () => {
    const documentTypeLabel =
      documentType?.text ??
      documentOptions?.find((opt) => opt.key === documentData?.entityDocumentTypeId)
        ?.text;


    return (
      <PanelHeader
        hasHeaderText={false}
        onClose={() => {
          clearData();
          onDismiss();
        }}
      >
        <>
          <Stack grow horizontal horizontalAlign="space-between">
            <Stack tokens={{ childrenGap: 10 }}>
              <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                <Text variant="xLarge">Document</Text>
                {documentTypeLabel && (
                  <Text variant="xLarge" className={commonStyles.colorThemePrimary}>
                    {documentTypeLabel}
                  </Text>
                )}
              </Stack>

              {!!documentData?.documentFileDistributionsByDocumentFileId
                ?.totalCount &&
                documentData.documentFileDistributionsByDocumentFileId
                  .totalCount > 0 && (
                  <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign="center">
                    <Text
                      className={styles.accountingDetailsText}
                      id={calloutId}
                      onMouseEnter={() => setIsCalloutVisible(true)}
                      onMouseLeave={() => setIsCalloutVisible(false)}
                    >
                      Accounting Details
                    </Text>
                    {isCalloutVisible && (
                      <Callout
                        gapSpace={0}
                        target={`#${calloutId}`}
                        directionalHint={DirectionalHint.leftTopEdge}
                        onDismiss={() => setIsCalloutVisible(false)}
                      >
                        <Stack>
                          <AccountingDetails
                            data={
                              documentData.documentFileDistributionsByDocumentFileId
                            }
                          />
                        </Stack>
                      </Callout>
                    )}
                  </Stack>
                )}
            </Stack>

            {documentData?.id && (
              <Stack horizontal tokens={{ childrenGap: 10 }}>
                <IconButton
                  iconProps={{ iconName: 'View' }}
                  onClick={() => {
                    const { fileReference, id, _fileType } = { ...documentData };
                    setDocViewState({
                      isOpen: true,
                      title: fileReference,
                      entityDocumentId: id,
                      _fileType: _fileType!,
                    });
                  }}
                />
                <ActionMessageModal
                  entityType={EntityType.Document}
                  action={EntityAction.Delete}
                  onConfirm={handleDeleteDocument}
                  disabled={documentData?._isDeletable === false || deleteLoading}
                />
              </Stack>
            )}

          </Stack>
        </>
      </PanelHeader>
    );
  };


  return (
    <Stack>
      <Formik<EntityDocumentValues>
        enableReinitialize
        initialValues={initialValues}
        validateOnMount
        validationSchema={validationSchema}
        onSubmit={handleUpdate}
      >
        {({ submitForm, isSubmitting, dirty, resetForm, errors, values }) => (
          <Form>
            <DraggablePanel
              {...PanelCommonProps}

              initialWidth={900}
              minWidth={900}
              isBlocking={false}
              onRenderHeader={_onRenderHeader}
              onRenderFooter={() => (
                <FooterActionBar
                  showButtons={{
                    showCreateNewButton: false,
                    showOnSaveButton: false,
                  }}
                  disabled={
                    updateDocLoading || {
                      save: !dirty || Object.keys(errors).length > 0,
                      cancel: !dirty,
                    }
                  }
                  isSubmitting={isSubmitting || retireBalanceLoading}
                  onCancel={() => {
                    resetForm();
                    clearData();
                    onDismiss();
                  }}
                  onSave={async () => {
                    await submitForm();
                    resetForm();
                    clearData();
                    onDismiss();
                  }}
                  children={
                    <Stack className={styles.footerStyles}>
                      {(updateDocLoading || retireBalanceLoading) && <ProgressIndicator />}
                      <Stack
                        horizontalAlign="space-between"
                        horizontal
                        tokens={{ childrenGap: 6, padding: 25 }}
                      >
                        <Stack
                          horizontal
                          tokens={{
                            childrenGap: 20,
                            padding: '0px 0px 0px 20px',
                          }}
                        >
                          <PrimaryButton
                            disabled={
                              !dirty ||
                              Object.keys(errors).length > 0 ||
                              isSubmitting
                            }
                            text="Save"
                            onClick={async () => {
                              await submitForm();
                              resetForm();
                              clearData();
                              onDismiss();
                            }}
                          />
                          <DefaultButton
                            onClick={() => {
                              resetForm();
                              clearData();
                              onDismiss();
                            }}
                            text="Cancel"
                          />
                        </Stack>

                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                          {documentData?.documentAppliedAmounts?._isRetiringBalanceAllowed &&
                            documentData?.documentAppliedAmounts?.usedTotal !== null &&
                            parseFloat(documentData?.documentAppliedAmounts?.usedTotal) > 0 && (
                              <PrimaryButton
                                disabled={retireBalanceLoading}
                                text="Retire Balance"
                                onClick={toggleConfirmDialog}
                              />
                            )}
                        </Stack>
                      </Stack>
                    </Stack>
                  }
                />
              )}
              isOpen
              onDismiss={() => {
                resetForm();
                clearData();
                onDismiss();
              }}
              isLightDismiss
            >
              <Stack
                tokens={{ childrenGap: 20 }}
                style={{ padding: '0px 25px 150px 25px' }}
                styles={{ root: { width: '100%' } }}
              >
                <DocumentDetailPanleFormFields
                  values={values}
                  fileSelected={fileSelected}
                  setFileSelected={setFileSelected}
                  documentOptions={documentOptions}
                  companyCurrencies={companyCurrencies}
                  documentData={documentData}
                  defaultDocumentTypeId={defaultDocumentTypeId}
                  isDocumentProtected={isDocumentProtected}
                  isEdit
                  documentType={documentType}
                  setDocumentType={setDocumentType}
                  isAccountingDocument={isAccountingDocument}
                  setIsAccountingDocument={setIsAccountingDocument}
                  isDisabled={isDisabled}
                  setIsDisabled={setIsDisabled}
                />
              </Stack>
            </DraggablePanel>
          </Form>
        )}
      </Formik>

      <ConfirmDialog
        hidden={hideConfirmDialog}
        title="Are you sure you want to retire balance?"
        subText=""
        onDismiss={toggleConfirmDialog}
        onConfirm={async () => {
          toggleConfirmDialog();
          const { errors } = await retireBalance({
            variables: {
              input: {
                entityDocumentId: documentData?.id!,
                rowTimestamp: documentData?._rowTimestamp!,
              },
            },
          });
          if (errors?.length)
            showCustomToast({ content: errors[0].message, appearance: 'error' });
          else {
            showCustomToast({ content: 'Balance retired', appearance: 'success' });
            onDismiss();
          }
        }}
      />
      <DocumentViewModal
        paddingTop={0}
        paddingLeft={documentViewPosition}
        onDismiss={() =>
          setDocViewState({ isOpen: false, _fileType: 'pdf' })
        }
        {...docViewState}
      />
    </Stack>
  );
};